import { IFinOpsBaseline, IFinOpsBudgetAllocationCombined } from "../../../models/FinOpsBudgets";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "@fluentui/react-components";
import { classifyCategory, getUTCMonthStartTime, isAIService, isNonAIService } from "../../../utils/BudgetUtils";

import React from "react";
import { currencyFormatter } from "../../../utils/currency";
import styles from "./SummaryTable.less"

interface SummaryTableProps {
  previousFiscalYear: string;
  currentFiscalYear: string;
  currentMonth: Date;
  data: IFinOpsBudgetAllocationCombined[];
  baselineData: IFinOpsBaseline[];
  isLoadingBudgetData: boolean;
  viewLevel?: string;
}

const SummaryTable: React.FC<SummaryTableProps> = ({ previousFiscalYear, currentFiscalYear, currentMonth, data, baselineData, viewLevel  }) => {
  const deduplicateData = (
      data: IFinOpsBudgetAllocationCombined[]
  ): IFinOpsBudgetAllocationCombined[] => {
    const seen = new Map<string, IFinOpsBudgetAllocationCombined>();

    for (const item of data) {
      const key = `${item.month}|${item.serviceId}|${item.category}|${item.type}`;
      if (!seen.has(key)) {
        seen.set(key, item);
      }
    }

    return Array.from(seen.values());
  };

  const deduplicateBaseline = (
    data: IFinOpsBaseline[]
  ): IFinOpsBaseline[] => {
    const seen = new Map<string, IFinOpsBaseline>();

    for (const item of data) {
      const key = `${item.month}|${item.serviceId}|${item.category}|${item.type}`;
      if (!seen.has(key)) {
        seen.set(key, item);
      }
    }

    return Array.from(seen.values());
  };

  const dedupedData = deduplicateData(data);
  const dedupedBaseline = deduplicateBaseline(baselineData);

  const tableHeaders = [
    "",
    `${previousFiscalYear} Baseline`,
    `${currentFiscalYear} Budget Ask`,
    `${currentFiscalYear} Actual Cost`,
    `${currentFiscalYear} ForeCast Cost`,
    `${currentFiscalYear} Budget Gap`,
  ];

  const table1RowsData = ["AI", "Non-AI"].map((label) => {
    const filterFn =
      label === "AI" ? isAIService : isNonAIService;

  const previousFiscalYearSpend = dedupedBaseline.filter((d) => filterFn(d)).reduce((sum, d) => sum + (d.cost ?? 0), 0);
  const currentFiscalYearBudgetAsk = dedupedData.filter((d) => filterFn(d)).reduce((sum, d) => sum + (d.budget ?? 0), 0);
  const currentFiscalYearActual = dedupedData.filter((d) => d.month && filterFn(d) &&  getUTCMonthStartTime(d.month) < getUTCMonthStartTime(currentMonth)).reduce((sum, d) => sum + (d.cost ?? 0), 0);
  const currentFiscalYearForecast = dedupedData.filter((d) => d.month && filterFn(d) &&  getUTCMonthStartTime(d.month) >= getUTCMonthStartTime(currentMonth)).reduce((sum, d) => sum + (d.cost ?? 0), 0);

    return {
      label,
      values: [
        0, // Placeholder for Metric Column
        previousFiscalYearSpend,
        currentFiscalYearBudgetAsk,
        currentFiscalYearActual,
        currentFiscalYearForecast,
        (currentFiscalYearActual + currentFiscalYearForecast) - currentFiscalYearBudgetAsk,
      ],
    };
  });

  const total1Row = {
    label: "Total",
    values: table1RowsData[0].values.map((_, i) =>
      table1RowsData.reduce((sum, row) => sum + row.values[i], 0)
    ),
  };
  table1RowsData.push(total1Row);

  const secondCategories = ["Azure", "Cosmic", "MCIO Network", "Passive Monitoring",];

  const table2RowsData = secondCategories.map((label) => {
    const previousFiscalYearSpend = dedupedBaseline.filter((d) => classifyCategory(d) === label).reduce((sum, d) => sum + (d.cost ?? 0), 0);
    const currentFiscalYearBudgetAsk = dedupedData.filter((d) => classifyCategory(d) === label).reduce((sum, d) => sum + (d.budget ?? 0), 0);
    const currentFiscalYearActual = dedupedData.filter((d) => d.month && classifyCategory(d) === label &&  getUTCMonthStartTime(d.month) < getUTCMonthStartTime(currentMonth)).reduce((sum, d) => sum + (d.cost ?? 0), 0);
    const currentFiscalYearForecast = dedupedData.filter((d) => d.month && classifyCategory(d) === label &&  getUTCMonthStartTime(d.month) >= getUTCMonthStartTime(currentMonth)).reduce((sum, d) => sum + (d.cost ?? 0), 0);
    return {
      label,
      values: [
        0, // Placeholder for Metric Column
        previousFiscalYearSpend,
        currentFiscalYearBudgetAsk,
        currentFiscalYearActual,
        currentFiscalYearForecast,
        (currentFiscalYearActual + currentFiscalYearForecast) - currentFiscalYearBudgetAsk,
      ],
    };
  });
  const total2Row = {
    label: "Total",
    values: table2RowsData[0].values.map((_, i) =>
      table2RowsData.reduce((sum, row) => sum + row.values[i], 0)
    ),
  };
  table2RowsData.push(total2Row);

  if (viewLevel === "service") {
    return (
      <div className={styles.sumaryTableRow}>
        <div className={styles.summaryTableContainer}>
          <div className={styles.summaryTableWrapper}>
            <h2 className={styles.budgetTableTitle}>Category Overview</h2>
            <Table className={styles.budgetTable} aria-label="Category Overview">
              <TableHeader>
                <TableRow>
                  {tableHeaders.map((header, index) => (
                    <TableHeaderCell key={index}>{header}</TableHeaderCell>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {table2RowsData.map((row, rowIndex) => (
                  <TableRow key={rowIndex}>
                    {row.values.map((value, colIndex) => (
                      <TableCell key={colIndex}>
                        {row.label && colIndex === 0 ? (
                          <strong>{row.label}</strong>
                        ) : (
                          currencyFormatter(value as number)
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    );
  }
  else {
    return (
      <div className={styles.sumaryTableRow}>
        <div className={styles.summaryTableContainer}>
          <div className={styles.summaryTableWrapper}>
            <h2 className={styles.budgetTableTitle}>AI vs Non-AI Overview</h2>
            <Table className={styles.budgetTable} aria-label="AI vs Non-AI">
              <TableHeader>
                <TableRow>
                  {tableHeaders.map((header, index) => (
                    <TableHeaderCell key={index}>{header}</TableHeaderCell>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {table1RowsData.map((row, rowIndex) => (
                  <TableRow key={rowIndex}>
                    {row.values.map((value, colIndex) => (
                      <TableCell key={colIndex}>
                        {row.label && colIndex === 0 ? (
                          <strong>{row.label}</strong>
                        ) : (
                          currencyFormatter(value as number)
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
        <div className={styles.summaryTableContainer}>
          <div className={styles.summaryTableWrapper}>
            <h2 className={styles.budgetTableTitle}>Category Overview</h2>
            <Table className={styles.budgetTable} aria-label="Category Overview">
              <TableHeader>
                <TableRow>
                  {tableHeaders.map((header, index) => (
                    <TableHeaderCell key={index}>{header}</TableHeaderCell>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {table2RowsData.map((row, rowIndex) => (
                  <TableRow key={rowIndex}>
                    {row.values.map((value, colIndex) => (
                      <TableCell key={colIndex}>
                        {row.label && colIndex === 0 ? (
                          <strong>{row.label}</strong>
                        ) : (
                          currencyFormatter(value as number)
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    );
  }
};

export default SummaryTable;